import './confirm-cid.scss'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import Header from '../../components/collections/Header'
import { CoverSpinning } from '../../components/elements'
import Modal from '../../components/modals/Modal'
import TagManager from '../../lib/tag-manager'
import { isValidCID } from '../../lib/utils'
import { ActionType } from '../../providers/action'
import { useAppState, useDispatcher } from '../../providers/store'
import getApi from '../../services'

interface FormInput {
    cid: string
}

interface NoticeModalProps {
    title: string
    message: string
    toggle(e: React.SyntheticEvent<HTMLButtonElement>): void
}

const NoticeModal: React.FC<NoticeModalProps> = ({ title, message, toggle }) => {
    return (
        <Modal>
            <div className="notice-modal-content abcs-container">
                <header>
                    <h4>{title}</h4>
                    <p>{message}</p>
                </header>

                <button type="button" className="abcs-button fluid primary" onClick={toggle}>
                    รับทราบ
                </button>
            </div>
        </Modal>
    )
}

const ConfirmLastDigitsCID: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [notice, setNotice] = useState<{
        shown: boolean
        title: string
        description: string
    }>({ shown: false, title: '', description: '' })
    const dispatch = useDispatcher()
    const appState = useAppState()
    const navigate = useNavigate()
    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
    } = useForm<FormInput>()
    const cidField = register('cid', {
        required: {
            value: true,
            message: 'กรุณาระบุหมายเลขประจำตัวประชาชน',
        },
        pattern: /[0-9]*/gm,
        validate: (value: string) => {
            if (process.env.DISABLED_REAL_CID_CHECKED === 'true') {
                return true
            }
            if (!isValidCID(value)) {
                return 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและดำเนินการอีกครั้ง'
            }
            return true
        },
    })

    const onSubmit = async ({ cid }: FormInput) => {
        try {
            setLoading(true)
            const result = await getApi().ptpService.checkCID(cid)
            dispatch({ type: ActionType.SAVED_USER_INFO, payload: { user: result } })
        } catch (error) {
            const title = error.status === 404 ? 'ไม่พบข้อมูลในระบบ' : 'เกิดข้อผิดพลาด'
            const description = 'กรุณาลองใหม่อีกครั้ง'
            setNotice({ shown: true, title, description })
        } finally {
            setLoading(false)
        }
    }

    useMemo(() => {
        TagManager.loadEvent({ page: 'promise-to-pay-verification-cid' })
    }, [])

    // NOTE: Fix memory leak
    useEffect(() => {
        if (appState.user) {
            navigate('/promise-to-pay/personal-info')
        }
    }, [appState.user, navigate])

    return (
        <>
            {loading && <CoverSpinning />}
            {notice.shown && (
                <NoticeModal
                    title={notice.title}
                    message={notice.description}
                    toggle={() => setNotice({ shown: false, description: '', title: '' })}
                />
            )}
            <div className="confirm-cid">
                <Header
                    title="การชำระค่างวด"
                    messages={['ยังทำรายการไม่เสร็จ กรุณาทำรายการใหม่อีกครั้ง']}
                />
                <div className="abcs-container">
                    <section className="information">
                        <h5>แบบฟอร์มกรณีไม่สามารถจ่ายค่างวดตามเวลา</h5>
                        <p>
                            กรุณากรอกข้อมูลในแบบฟอร์ม
                            บริษัทจะใช้เพื่อติดต่อสอบถามการชำระค่างวดกับท่านใกล้วันนัดหมายค่ะ
                        </p>
                        <p style={{ fontWeight: 'bold' }}>
                            อย่างไรก็ดี การกรอกข้อมูลนี้ไม่สามารถเปลี่ยนแปลงวันนัดชำระได้
                            กรุณาชำระค่างวดตรงกับวันนัดตามสัญญาเพื่อรักษาเครดิต
                        </p>
                    </section>
                    <div className={`abcs-input ${errors.cid && 'error'} fluid`}>
                        <label htmlFor="cid">กรอกหมายเลขบัตรประชาชน เพื่อยืนยันตัวตน</label>
                        <input
                            id="cid"
                            maxLength={13}
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            {...cidField}
                            onBlur={(e) => {
                                cidField.onBlur(e)
                                trigger()
                            }}
                        />
                        {errors.cid && <small>{errors.cid.message}</small>}
                    </div>
                    <footer className="footer">
                        <button
                            className={`abcs-button primary  fluid ${errors.cid && 'disabled'}`}
                            type="submit"
                            onClick={handleSubmit(onSubmit)}
                        >
                            ต่อไป
                        </button>
                    </footer>
                </div>
            </div>
        </>
    )
}

export default ConfirmLastDigitsCID
